import { requests } from '../../helper/agent';
import * as API_ROUTES from '../../routing/api';
import { userLogout } from './user';
import { SubmissionError } from 'redux-form/lib/SubmissionError';
import { parseApiErrors } from '../../helper/apiUtils';
import {
  PATIENT_NOTE_ADD_ERROR,
  PATIENT_NOTE_ADD_REQUEST,
  PATIENT_NOTE_ADDED,
  PATIENT_NOTE_DELETED,
  PATIENT_NOTE_UPDATED,
  PATIENT_NOTES_ERROR,
  PATIENT_NOTES_RECEIVED,
  PATIENT_NOTES_REQUEST,
  PATIENT_NOTES_UNLOAD,
} from '../constants';

/**
 * MED-922. A patient note is a PatientNote, never an InvoiceNote - even when it is written from
 * the invoice screen. These post to /patient_notes with a patient IRI; the invoice-note actions
 * post to /invoice_notes with an invoice IRI. Do not merge them.
 */

export const patientNotesRequest = () => ({ type: PATIENT_NOTES_REQUEST });
export const patientNotesReceived = (data, patientId) => ({ type: PATIENT_NOTES_RECEIVED, data, patientId });
export const patientNotesError = (error) => ({ type: PATIENT_NOTES_ERROR, error });
export const patientNotesUnload = () => ({ type: PATIENT_NOTES_UNLOAD });

/**
 * Consultant-side users are refused these endpoints outright (403 on the collection, 404 on an
 * item, because the query extension answers before the security expression). Neither is a
 * SubmissionError case on a read - the panel simply shows nothing, and those users never reach a
 * screen that mounts it.
 */
export const patientNotesFetch = (patientId) => {
  return (dispatch) => {
    dispatch(patientNotesRequest());

    return requests
      .get(API_ROUTES.PATIENT_NOTES.replace(':id', patientId), { pagination: false })
      .then((response) => dispatch(patientNotesReceived(response, Number(patientId))))
      .catch((error) => {
        if (error.response && error.response.status === 401) {
          return dispatch(userLogout());
        }
        dispatch(patientNotesError(parseApiErrors(error)));
      });
  };
};

export const patientNoteAddRequest = () => ({ type: PATIENT_NOTE_ADD_REQUEST });
export const patientNoteAdded = (data) => ({ type: PATIENT_NOTE_ADDED, data });
export const patientNoteAddError = (error) => ({ type: PATIENT_NOTE_ADD_ERROR, error });

export const patientNoteAdd = (patientId, note, isPopup = false) => {
  return (dispatch) => {
    dispatch(patientNoteAddRequest());

    return requests
      .post(API_ROUTES.PATIENT_NOTE, {
        patient: `${API_ROUTES.PATIENTS}/${patientId}`,
        note,
        isPopup: Boolean(isPopup),
      })
      .then((response) => dispatch(patientNoteAdded(response)))
      .catch((error) => {
        dispatch(patientNoteAddError(error));
        if (error.response && error.response.status === 401) {
          return dispatch(userLogout());
        } else if (error.response && error.response.status === 403) {
          throw new SubmissionError(parseApiErrors({ _error: 'You are not allowed to do this.' }));
        }
        throw new SubmissionError(parseApiErrors(error));
      });
  };
};

export const patientNoteUpdated = (data) => ({ type: PATIENT_NOTE_UPDATED, data });

/**
 * The only way a popup stops. No acknowledgement state exists anywhere - this clears the flag for
 * everyone, which is the ruling.
 */
export const patientNoteSetPopup = (noteId, isPopup) => {
  return (dispatch) => {
    return requests
      .patch(`${API_ROUTES.PATIENT_NOTE}/${noteId}`, { isPopup: Boolean(isPopup) })
      .then((response) => dispatch(patientNoteUpdated(response)))
      .catch((error) => {
        if (error.response && error.response.status === 401) {
          return dispatch(userLogout());
        }
        throw new SubmissionError(parseApiErrors(error));
      });
  };
};

export const patientNoteDeleted = (id) => ({ type: PATIENT_NOTE_DELETED, id });

export const patientNoteDelete = (noteId) => {
  return (dispatch) => {
    return requests
      .delete(`${API_ROUTES.PATIENT_NOTE}/${noteId}`)
      .then(() => dispatch(patientNoteDeleted(noteId)))
      .catch((error) => {
        if (error.response && error.response.status === 401) {
          return dispatch(userLogout());
        }
        throw new SubmissionError(parseApiErrors(error));
      });
  };
};
